import { Box, Button, Card, CardContent, CardHeader, Divider, Grid, IconButton, MenuItem, TextField, Theme, Typography, useTheme } from '@mui/material';
import { ClearIcon, DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { PatientsListRequest, PatientsListRequestForm } from 'models/patients.model';
import { FC, useCallback } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { convertDate } from 'utils/func/Date.func';
import { useQuery } from 'hooks';
import PrescriptionsAccordion from 'pages/doctor/prescriptions/PrescriptionsAccordion';

interface FindPatientsFormProps {
  useAccordion?: boolean;
  handleFormAction: (data: PatientsListRequest) => void;
}

const CNS_MIN_LENGTH = 8;
const NAME_MIN_LENGTH = 2;

const FindPatientsForm: FC<FindPatientsFormProps> = ({ useAccordion, handleFormAction }) => {
  const { t } = useTranslation();
  const query = useQuery();
  const theme = useTheme();

  const { register, handleSubmit, control, formState, setValue, reset, watch } = useForm<PatientsListRequestForm>({ defaultValues: { cns: query.get('cns') ?? '' } });

  const { cns, first_name, last_name, birth_date } = watch();
  const onSubmit: SubmitHandler<PatientsListRequestForm> = useCallback(
    (data) => {
      const { birth_date } = data;
      const birth_dateShort = birth_date ? convertDate(birth_date, false, 'yyyy-MM-dd') : '';

      handleFormAction({
        ...data,
        birth_date: birth_dateShort,
      });
    },
    [handleFormAction],
  );

  const isInvalidCNS = formState.isSubmitted && formState.dirtyFields['cns'] && cns?.length ? cns?.length < CNS_MIN_LENGTH : false;
  const isInvalidFirstName = formState.isSubmitted && formState.dirtyFields?.first_name && first_name?.length ? first_name?.length < NAME_MIN_LENGTH : false;
  const isInvalidLastName = formState.isSubmitted && formState.dirtyFields?.last_name && last_name?.length ? last_name?.length < NAME_MIN_LENGTH : false;

  const getMinLengthError = (isInvalid: boolean, count: number) => (isInvalid ? t('patients.search.errors.min_length', { count }) : '');

  const clearField = (name: keyof PatientsListRequestForm) => setValue(name, '', { shouldValidate: true, shouldTouch: true });

  const title = t('patients.search.title');

  const renderForm = () => (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Grid container item xs={12}>
        <Grid container item>
          <Grid item xs={12}>
            <TextField
              variant='filled'
              {...register('cns', { minLength: CNS_MIN_LENGTH })}
              label={t('patients.search.fields.cns')}
              sx={{ width: '100%' }}
              error={isInvalidCNS}
              helperText={getMinLengthError(isInvalidCNS, CNS_MIN_LENGTH)}
              InputProps={{
                endAdornment: (cns?.length || 0) > 0 && (
                  <IconButton onClick={() => clearField('cns')}>
                    <ClearIcon sx={{ color: `${theme.palette.error.main} !important` }} />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant='filled'
              {...register('last_name', { minLength: NAME_MIN_LENGTH })}
              label={`${t('patients.search.fields.patient_name')}`}
              error={isInvalidLastName}
              helperText={getMinLengthError(isInvalidLastName, NAME_MIN_LENGTH)}
              fullWidth
              InputProps={{
                endAdornment: (last_name?.length || 0) > 0 && (
                  <IconButton onClick={() => clearField('last_name')}>
                    <ClearIcon sx={{ color: `${theme.palette.error.main} !important` }} />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant='filled'
              {...register('first_name', { minLength: NAME_MIN_LENGTH })}
              label={`${t('patients.search.fields.patient_first_name')}`}
              error={isInvalidFirstName}
              helperText={getMinLengthError(isInvalidFirstName, NAME_MIN_LENGTH)}
              fullWidth
              InputProps={{
                endAdornment: (first_name?.length || 0) > 0 && (
                  <IconButton onClick={() => clearField('first_name')}>
                    <ClearIcon sx={{ color: `${theme.palette.error.main} !important` }} />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='birth_date'
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <DatePicker
                  label={`${t('patients.search.fields.birth_date')}`}
                  openTo='year'
                  disableFuture
                  views={['year', 'month', 'day']}
                  {...field}
                  slotProps={{ textField: { variant: 'filled', fullWidth: true } }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Grid>
      <Box
        sx={{
          marginTop: (theme) => theme.spacing(6),
        }}
      >
        <Button type='submit' variant='contained' disabled={!cns && !birth_date && !last_name && !first_name}>
          {t('patients.search.button')}
        </Button>
        {formState.isDirty && (
          <Button sx={{ ml: 2 }} onClick={() => reset()}>
            {t('patients.search.reset')}
          </Button>
        )}
      </Box>
    </form>
  );

  return useAccordion ? (
    <PrescriptionsAccordion title={title} children={renderForm()} />
  ) : (
    <Card
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        title={
          <Typography variant='h2' mb={4}>
            {t('patients.search.title')}
          </Typography>
        }
      />
      <CardContent sx={{ display: 'flex', flex: '1 0 0' }}>{renderForm()}</CardContent>
    </Card>
  );
};

export default FindPatientsForm;
